import { instanceOfGetDPSDetailResponse } from "@repo/api";
import { PensionContractType, setLastQuestionnaireSubmitDate, StepperType, useAppDispatch } from "@repo/logic";
import { FC, useEffect } from "react";
import { useTypedTranslation } from "../../../hooks/useTypedTranslation";
import { CustomStepper } from "../../Stepper";
import { useStepperController } from "../../Stepper/useStepperController";
import { QuestionnaireQueryType } from "../Questionnaire.types";
import { QuestionnairePages } from "../Questions";

export type PensionQuestionnaireStepExtraProps = {
    contractId: string;
    contractType?: PensionContractType;
    title?: string;
    name?: StepperType;
};

export type PensionQuestionnaireStepProps = PensionQuestionnaireStepExtraProps & QuestionnaireQueryType;

type PensionQuestionnaireStepperProps = {
    closeAction?: () => void;
} & PensionQuestionnaireStepProps;

export const PensionQuestionnaireStepper: FC<PensionQuestionnaireStepperProps> = ({
    closeAction,
    queries,
    contractId,
    contractType,
    title,
    name,
}) => {
    const { t } = useTypedTranslation();
    const { usePensionDetail } = queries;
    const dispatch = useAppDispatch();

    const { data: pensionDetailData, refetch: refetchPensionDetail } =
        usePensionDetail?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) }, type: contractType }) ??
        {};

    useEffect(() => {
        refetchPensionDetail?.();
    }, [refetchPensionDetail]);

    useEffect(() => {
        // Set last questionnaire submit date in stepper state when pension detail data is available
        if (pensionDetailData && instanceOfGetDPSDetailResponse(pensionDetailData)) {
            dispatch(
                setLastQuestionnaireSubmitDate(
                    pensionDetailData.questionnaireSubmitDate instanceof Date
                        ? pensionDetailData.questionnaireSubmitDate.toISOString()
                        : pensionDetailData.questionnaireSubmitDate,
                ),
            );
        }
    }, [dispatch, pensionDetailData]);

    const {
        externalBusy,
        activeStepIndex,
        activeStepKey,
        totalSteps,
        activeStep,
        overlayElement,
        showConfirmModal,
        hideConfirmModal,
        handleNext,
        handleBack,
        nextButtonText,
        onCloseHandler,
    } = useStepperController<PensionQuestionnaireStepExtraProps>({
        name: name ?? StepperType.PensionQuestionnaire,
        pages: QuestionnairePages,
        queries,
        extraProps: { contractId, contractType, title },
        onCloseConfirmed: closeAction,
    });

    return (
        <CustomStepper
            externalBusy={externalBusy}
            title={title ?? t("investmentStrategy.toChangeSavingsStrategy")}
            stepsCount={totalSteps}
            activeStepIndex={activeStepIndex}
            activeStepKey={activeStepKey}
            activeStep={activeStep}
            onNext={handleNext}
            onBack={handleBack}
            onClose={onCloseHandler}
            isLoading={false}
            nextButtonText={nextButtonText}
            overlay={overlayElement}
            showConfirmModal={showConfirmModal}
            hideConfirmModal={hideConfirmModal}
        />
    );
};
